if (!customElements.get("variant-picker")) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
    }

    get selectedOptionValues() {
      const options = this.querySelectorAll(
        "select, .variant-picker__field input[type='radio']"
      );
      const selectedValues = new Map();

      options.forEach((field) => {
        const fieldName = field.name || field.dataset.fieldName;

        if (field.tagName.toLowerCase() === "select") {
          const selectedOption = Array.from(field.options).find(
            (opt) => opt.selected && !opt.disabled
          );
          if (selectedOption) {
            selectedValues.set(fieldName, selectedOption.dataset.optionValueId);
          }
        } else if (
          field.tagName.toLowerCase() === "input" &&
          field.type === "radio"
        ) {
          const checkedInput = field.checked ? field : null;
          if (checkedInput) {
            selectedValues.set(fieldName, checkedInput.dataset.optionValueId);
          }
        }
      });

      options.forEach((field) => {
        const fieldName = field.name || field.dataset.fieldName;

        if (!selectedValues.has(fieldName)) {
          if (field.tagName.toLowerCase() === "select") {
            const firstValidOption = Array.from(field.options).find(
              (opt) => !opt.disabled
            );
            if (firstValidOption) {
              selectedValues.set(
                fieldName,
                firstValidOption.dataset.optionValueId
              );
            }
          } else if (
            field.tagName.toLowerCase() === "input" &&
            field.type === "radio"
          ) {
            const firstInput = field;
            if (firstInput) {
              selectedValues.set(fieldName, firstInput.dataset.optionValueId);
            }
          }
        }
      });

      const result = [];
      options.forEach((field) => {
        const fieldName = field.name || field.dataset.fieldName;
        if (selectedValues.has(fieldName)) {
          const optionValueId = selectedValues.get(fieldName);
          if (optionValueId && !result.includes(optionValueId)) {
            result.push(optionValueId);
          }
        }
      });
      return result;
    }

    getInputForEventTarget(target) {
      return target.tagName === "SELECT" ? target.selectedOptions[0] : target;
    }

    updateDesinasOptions() {
      const variants = this.desinasVariants;
      if (!variants?.length) return;

      const fields = [
        ...this.querySelectorAll(".variant-picker__field")
      ];

      if (
        !fields.length ||
        !variants.every(v =>
          Array.isArray(v.options) &&
          v.options.length === fields.length &&
          v.options.every(x => typeof x === "string")
        )
      ) return;

      if (fields.some(field =>
        !field.querySelector("select") &&
        [...field.querySelectorAll('input[type="radio"]')]
          .some(input =>
            !input.closest(".variant-picker__option")
          )
      )) return;

      const selected = [];
      const soldOutLabel = this.dataset.soldOutLabel || "";

      for (let index = 0; index < fields.length; index++) {
        const field = fields[index];
        const select = field.querySelector("select");

        const choices = select
          ? [...select.options]
          : [...field.querySelectorAll('input[type="radio"]')];

        const current = () => select
          ? choices.find(c => c.selected)
          : choices.find(c => c.checked);

        const matches = choice => variants.filter(v =>
          v.options[index] === choice.value &&
          selected.every(
            (value, i) => v.options[i] === value
          )
        );

        if (
          !choices.length ||
          !choices.some(c => matches(c).length)
        ) return;

        choices.forEach(choice => {
          const matching = matches(choice);
          const exists = matching.length > 0;
          const soldOut =
            exists && !matching.some(v => v.available);

          if (select) {
            choice.hidden = !exists;
            choice.disabled = !exists;
            choice.style.display = exists ? "" : "none";

            if (!choice.hasAttribute("data-desinas-label")) {
              choice.dataset.desinasLabel =
                choice.textContent.trim();
            }

            choice.textContent = soldOut
              ? `${choice.dataset.desinasLabel} – ${soldOutLabel}`
              : choice.dataset.desinasLabel;

            return;
          }

          const wrapper = choice.closest(
            ".variant-picker__option"
          );

          wrapper.hidden = !exists;
          wrapper.style.display = exists ? "" : "none";

          // Zests eigene Klassen wie beim Server-Rendering setzen,
          // damit die native Durchstreichung greift
          wrapper.classList.toggle(
            "variant-picker__option--soldout", soldOut
          );

          choice.classList.toggle("disabled", soldOut);
          choice.disabled = !exists;

          choice.setAttribute(
            "aria-label",
            soldOut
              ? `${choice.value} – ${soldOutLabel}`
              : choice.value
          );
        });

        const oldChoice = current();

        if (!oldChoice || !matches(oldChoice).length) {
          const available = choices.filter(c =>
            matches(c).some(v => v.available)
          );

          const pool = available.length
            ? available
            : choices.filter(c => matches(c).length);

          const isLength = /länge|length/i.test(
            field.dataset.optionName || ""
          );

          const oldNumber = Number.parseFloat(
            oldChoice?.value
          );

          const numericPool = pool.every(c =>
            /^\s*\d+(?:[.,]\d+)?\s*(?:cm)?\s*$/i.test(c.value)
          );

          const replacement =
            isLength &&
            Number.isFinite(oldNumber) &&
            numericPool
              ? [...pool].sort((a, b) =>
                  Math.abs(
                    Number.parseFloat(a.value) - oldNumber
                  ) -
                  Math.abs(
                    Number.parseFloat(b.value) - oldNumber
                  )
                )[0]
              : pool[0];

          if (replacement) {
            if (select) {
              select.value = replacement.value;
            } else {
              replacement.checked = true;
            }
          }
        }

        const value = current()?.value;
        if (value === undefined) return;

        selected.push(value);

        const display = field.querySelector(".selected-value");
        if (display) display.textContent = value;
      }
    }

    updateDesinasStockAlert() {
      const button = this.querySelector("[data-desinas-stock-alert]");
      if (!button) return;

      const variants = this.desinasVariants;
      const fields = [...this.querySelectorAll(".variant-picker__field")];

      let selectedVariant = null;

      if (
        Array.isArray(variants) &&
        variants.length &&
        fields.length &&
        variants.every(
          (v) => Array.isArray(v.options) && v.options.length === fields.length
        )
      ) {
        const selectedValues = fields.map((field) => {
          const select = field.querySelector("select");
          if (select) return select.value;
          return field.querySelector('input[type="radio"]:checked')?.value;
        });

        if (selectedValues.every((v) => typeof v === "string")) {
          selectedVariant = variants.find((variant) =>
            variant.options.every((value, index) => value === selectedValues[index])
          );
        }
      }

      // Nur für existierende, aber nicht kaufbare Varianten anzeigen
      const show = Boolean(selectedVariant && selectedVariant.available === false);

      button.hidden = !show;
      button.style.display = show ? "" : "none";
    }

    connectedCallback() {
      if (this.desinasListenerAttached) return;
      this.desinasListenerAttached = true;

      const init = () => {
        if (!this.isConnected) return;

        const script = this.querySelector("script[data-desinas-variants]");
        if (!script) return;

        // Combined Listings nicht beeinflussen
        if (this.querySelector('[data-product-url]:not([data-product-url=""])')) {
          return;
        }

        try {
          const data = JSON.parse(script.textContent);
          if (!Array.isArray(data) || !data.length) return;

          this.desinasVariants = data;
          this.updateDesinasOptions();
          this.updateDesinasStockAlert();
        } catch (error) {
          console.error("Desinas variant picker: invalid variant data", error);
        }
      };

      if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", init, { once: true });
      } else {
        requestAnimationFrame(init);
      }

      this.addEventListener("change", (event) => {
        this.updateDesinasOptions();
        this.updateDesinasStockAlert();

        const target = this.getInputForEventTarget(event.target);
        window.FoxThemeEvents.emit(PUB_SUB_EVENTS.optionValueSelectionChange, {
          data: {
            event,
            target,
            selectedOptionValues: this.selectedOptionValues,
          },
        });
      });

      // "Benachrichtige mich": Omnisend-Formular erst beim Klick öffnen
      this.addEventListener("click", (event) => {
        const button = event.target.closest("[data-desinas-stock-alert]");
        if (!button || !this.contains(button) || button.hidden) return;

        window.omnisend = window.omnisend || [];
        window.omnisend.push(["openForm", "6ad8bc08e6687a7367125538"]);
      });
    }
  }

  customElements.define("variant-picker", VariantPicker);
}
